import {
  toWorkspaceOption,
  FieldGroupingRule,
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsFields,
  PerspectivesGroupsWorkspace,
} from '@ardoq/perspectives';
import { Select } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ArdoqId } from '@ardoq/api-types';
import { RowHeader } from '../../atoms';
import { isTruthy } from '@ardoq/common-helpers';

type FieldGroupRuleProps = {
  groupingRule: FieldGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  fieldsPerWorkspace: Record<ArdoqId, PerspectivesGroupsFields[]>;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  tabIndex?: number;
  i18nLocale?: I18nLocale;
};

const toFieldOption = (field: PerspectivesGroupsFields) => ({
  value: field.id,
  label: field.name,
});

const FieldGroupRule = ({
  tabIndex,
  workspaces,
  groupingRule,
  fieldsPerWorkspace,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  i18nLocale = {},
}: FieldGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = [
    workspaceSelectOption,
    ...workspaces.map(workspace => toWorkspaceOption(workspace)),
  ]
    .filter(isTruthy)
    .map(option => ({
      ...option,
      ...(!fieldsPerWorkspace[option.value]?.length
        ? {
            isDisabled: true,
            popoverContent: t(
              i18nLocale,
              'perspectivesSidebar:groupingRules.noFieldsInWorkspace',
              'There are no fields in this workspace for the opened datasets'
            ),
          }
        : {}),
    }));

  const fields = fieldsPerWorkspace[groupingRule.workspaceId!] ?? [];
  const fieldOptions = [
    ruleTargetSelectOption,
    ...fields.map(toFieldOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.inWorkspace',
          'In workspace'
        )}
      </RowHeader>
      <Select
        value={groupingRule.workspaceId}
        dataTestId="field-grouping-rule-workspace-select"
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultFieldId = fieldsPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultFieldId,
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(i18nLocale, 'perspectivesSidebar:groupingRules.type', 'Type')}
      </RowHeader>
      <Select
        options={fieldOptions}
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.fieldsAriaLabel',
          'Fields'
        )}
        dataTestId="field-grouping-rule-field-select"
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
        menuPortalTarget={null}
      />
    </>
  );
};

export { FieldGroupRule };
